import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonPadraoComponent } from '../../shared/components/button-padrao/button-padrao.component';
import { FormFieldComponent } from '../../shared/components/form-field/form-field.component';
import { JsonEditorComponent } from '../../shared/components/json-editor/json-editor.component';
import { SelectOneComponent } from '../../shared/components/select-one/select-one.component';
import { SlideTogglePadraoComponent } from '../../shared/components/slide-toggle-padrao/slide-toggle-padrao.component';
import { IntegracaoModel, IntegracaoVariavelModel } from '../../shared/model/integracao-model';
import { ModalConfirmacaoComponent } from '../../shared/modais/modal-confirmacao/modal-confirmacao.component';
import { IntegracaoService } from '../../shared/service/integracao.service';
import { MsgService } from '../../shared/service/msg.service';

class OpcaoVariavel {
  id = '';
  nome = '';
  disabled = false;
}

class IntegracaoView extends IntegracaoModel {
  chave = 0;
  novo = false;
}

@Component({
  selector: 'app-card-integracao',
  standalone: true,
  imports: [
    ButtonPadraoComponent,
    FormFieldComponent,
    JsonEditorComponent,
    MatTooltip,
    SelectOneComponent,
    SlideTogglePadraoComponent
  ],
  templateUrl: './card-integracao.component.html',
  styleUrl: './card-integracao.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardIntegracaoComponent implements OnInit {

  @ViewChild('editorPayload') private editorPayload?: JsonEditorComponent;
  @ViewChild('toggleTipo') private toggleTipo?: SlideTogglePadraoComponent;

  private readonly integracaoService = inject(IntegracaoService);
  private readonly msgService = inject(MsgService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly integracoes = signal<IntegracaoView[]>([]);
  readonly integracaoSelecionada = signal<IntegracaoView | null>(null);
  readonly carregando = signal(false);
  readonly carregandoMais = signal(false);
  readonly possuiMais = signal(true);
  readonly salvando = signal(false);
  readonly payloadValido = signal(true);

  readonly opcoesVariaveis: OpcaoVariavel[] = [
    { id: 'NOME', nome: 'Nome', disabled: false },
    { id: 'CPF', nome: 'CPF', disabled: false },
    { id: 'TELEFONE', nome: 'Telefone', disabled: false },
    { id: 'EMAIL', nome: 'E-mail', disabled: false }
  ];

  readonly motivoBloqueio = computed<string | null>(() => {
    const integracao = this.integracaoSelecionada();
    if (!integracao) return 'Adicione ou selecione uma integração.';
    if (!integracao.nome?.trim()) return 'Informe o nome da integração.';
    if (!this.urlValida(integracao.url)) return 'Informe uma URL http(s) válida.';
    if (!integracao.token?.trim()) return 'Informe o token da integração.';

    if (!integracao.personalizada) {
      if (!integracao.payload?.trim()) return 'Informe o payload da integração.';
      if (!this.payloadValido() || !this.jsonObjetoValido(integracao.payload)) {
        return 'Corrija o JSON do payload antes de salvar.';
      }
      if (!this.jsonObjetoPossuiCampos(integracao.payload)) {
        return 'Informe ao menos um campo no payload da integração.';
      }

      const variaveis = integracao.variaveis ?? [];
      if (variaveis.some(variavel => !variavel.ordem || !variavel.campo)) {
        return 'Selecione o campo de todas as variáveis adicionadas.';
      }
      const ordens = new Set<number>(variaveis.map(variavel => variavel.ordem!));
      const marcadores = this.obterMarcadores(integracao.payload);
      if (ordens.size !== marcadores.size || [...ordens].some(ordem => !marcadores.has(ordem))) {
        return 'Inclua no JSON um marcador para cada variável, como "{{1}}".';
      }
    }
    return null;
  });

  private contadorChave = 0;
  private proximaPagina = 0;
  private cicloCarregamento = 0;
  private readonly tamanhoPagina = 20;

  ngOnInit() {
    this.reiniciarCarregamento();
  }

  carregarIntegracoes() {
    if (this.carregando() || this.carregandoMais() || !this.possuiMais()) return;

    const primeiraPagina = this.proximaPagina === 0;
    const cicloAtual = this.cicloCarregamento;
    if (primeiraPagina) {
      this.carregando.set(true);
    } else {
      this.carregandoMais.set(true);
    }

    this.integracaoService.buscarDoGrupo(this.proximaPagina, this.tamanhoPagina)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resposta => {
          if (cicloAtual !== this.cicloCarregamento) return;

          const recebidas = (resposta.content ?? []).map(item => this.paraView(item));
          this.integracoes.update(atuais => {
            const idsAtuais = new Set(
              atuais.map(item => item.id).filter((id): id is number => id !== undefined)
            );
            return [...atuais, ...recebidas.filter(item => !item.id || !idsAtuais.has(item.id))];
          });
          this.proximaPagina = resposta.number + 1;
          this.possuiMais.set(this.proximaPagina < resposta.totalPages);
          this.carregando.set(false);
          this.carregandoMais.set(false);
        },
        error: erro => {
          if (cicloAtual !== this.cicloCarregamento) return;
          this.carregando.set(false);
          this.carregandoMais.set(false);
          this.msgService.msgErro(
            erro?.error?.message ?? 'Não foi possível carregar as integrações deste grupo.'
          );
        }
      });
  }

  adicionarIntegracao() {
    const existente = this.integracoes().find(item => item.novo);
    if (existente) {
      this.integracaoSelecionada.set(existente);
      this.msgService.msgAdvertencia('Conclua ou cancele a nova integração antes de adicionar outra.');
      return;
    }

    const nova = new IntegracaoView();
    nova.chave = this.contadorChave++;
    nova.novo = true;
    nova.nome = '';
    nova.personalizada = false;
    nova.url = '';
    nova.token = '';
    nova.payload = '{\n  \n}';
    nova.variaveis = [];

    this.integracoes.update(itens => [nova, ...itens]);
    this.integracaoSelecionada.set(nova);
    this.payloadValido.set(true);
  }

  selecionarIntegracao(integracao: IntegracaoView) {
    this.integracaoSelecionada.set(integracao);
    this.payloadValido.set(integracao.personalizada || this.jsonObjetoValido(integracao.payload));
  }

  atualizarCampo(campo: 'nome' | 'url' | 'token' | 'payload', valor: string) {
    this.atualizarSelecionada(integracao => {
      integracao[campo] = valor;
      if (campo === 'payload' && !integracao.personalizada) {
        this.sincronizarVariaveisDoPayload(integracao, valor);
      }
    });
  }

  private sincronizarVariaveisDoPayload(integracao: IntegracaoView, payload: string) {
    const ordensExistentes = new Set(
      (integracao.variaveis ?? []).map(variavel => variavel.ordem)
    );
    const novasOrdens = [...this.obterMarcadores(payload)]
      .filter(ordem => !ordensExistentes.has(ordem))
      .sort((a, b) => a - b);

    if (!novasOrdens.length) return;
    integracao.variaveis = [
      ...(integracao.variaveis ?? []),
      ...novasOrdens.map(ordem => ({ ordem, campo: '' }))
    ];
  }

  atualizarTipo(padrao: boolean) {
    const integracao = this.integracaoSelecionada();
    if (!integracao) return;

    const padraoAtual = !integracao.personalizada;
    if (padrao === padraoAtual) return;

    const vaiDescartarPayload = !padrao
      && padraoAtual
      && this.payloadPossuiConteudo(integracao.payload);
    if (vaiDescartarPayload) {
      if (this.toggleTipo) {
        this.toggleTipo.valor = padraoAtual;
      }
      const dialogRef = this.dialog.open(ModalConfirmacaoComponent, {
        data: {
          texto: 'O payload configurado será perdido.',
          subTexto: 'Deseja alterar a integração para Personalizada?'
        }
      });
      dialogRef.afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirmado => {
          if (confirmado) {
            this.aplicarTipo(padrao);
          }
        });
      return;
    }

    this.aplicarTipo(padrao);
  }

  private aplicarTipo(padrao: boolean) {
    this.atualizarSelecionada(integracao => {
      integracao.personalizada = !padrao;
      if (integracao.personalizada) {
        integracao.payload = '{}';
        integracao.variaveis = [];
      }
    });
    this.payloadValido.set(true);
  }

  adicionarVariavel() {
    const integracao = this.integracaoSelecionada();
    if (!integracao) return;

    const maiorOrdem = (integracao.variaveis ?? []).reduce(
      (maior, item) => Math.max(maior, item.ordem ?? 0),
      0
    );
    const proximaOrdem = maiorOrdem + 1;

    const marcador = this.montarMarcador(proximaOrdem);
    if (!this.editorPayload?.inserirNoCursor(marcador)) {
      this.msgService.msgAdvertencia(
        'Posicione o cursor dentro do valor textual do payload onde deseja inserir a variável.'
      );
      return;
    }

    this.atualizarSelecionada(item => {
      item.variaveis = [...(item.variaveis ?? []), { ordem: proximaOrdem, campo: '' }];
    });
  }

  selecionarVariavel(ordem: number | undefined, opcao?: OpcaoVariavel) {
    if (!ordem || !opcao) return;
    this.atualizarSelecionada(integracao => {
      integracao.variaveis = (integracao.variaveis ?? []).map(variavel =>
        variavel.ordem === ordem ? { ...variavel, campo: opcao.id } : variavel
      );
    });
  }

  removerVariavel(ordem: number | undefined) {
    this.atualizarSelecionada(integracao => {
      integracao.variaveis = (integracao.variaveis ?? [])
        .filter(variavel => variavel.ordem !== ordem);
      if (ordem) {
        const marcador = this.montarMarcador(ordem);
        integracao.payload = (integracao.payload ?? '').split(marcador).join('');
      }
    });
  }

  obterOpcao(campo?: string): OpcaoVariavel | undefined {
    return this.opcoesVariaveis.find(opcao => opcao.id === campo);
  }

  montarMarcador(ordem?: number): string {
    return `{{${ordem ?? ''}}}`;
  }

  aoRolarLista(evento: Event) {
    const lista = evento.target as HTMLElement;
    const possuiRolagemVertical = lista.scrollHeight > lista.clientHeight;
    const proximoDoFim = possuiRolagemVertical
      ? lista.scrollTop + lista.clientHeight >= lista.scrollHeight - 48
      : lista.scrollLeft + lista.clientWidth >= lista.scrollWidth - 48;
    if (proximoDoFim) {
      this.carregarIntegracoes();
    }
  }

  salvar() {
    const integracao = this.integracaoSelecionada();
    const motivo = this.motivoBloqueio();
    if (!integracao || motivo) {
      this.msgService.msgAdvertencia(motivo ?? 'Selecione uma integração.');
      return;
    }
    if (this.salvando()) return;

    const dto = new IntegracaoModel();
    dto.id = integracao.id;
    dto.nome = integracao.nome?.trim();
    dto.personalizada = Boolean(integracao.personalizada);
    dto.url = integracao.url?.trim();
    dto.token = integracao.token?.trim();
    dto.payload = integracao.personalizada ? '{}' : integracao.payload;
    dto.variaveis = integracao.personalizada ? [] : (integracao.variaveis ?? []).map(variavel => ({
      ordem: variavel.ordem,
      campo: variavel.campo
    }));

    this.salvando.set(true);
    this.integracaoService.salvar(dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resposta => {
          const salva = this.paraView(resposta);
          salva.chave = integracao.chave;
          this.integracoes.update(itens => {
            const indice = itens.findIndex(item =>
              item.chave === integracao.chave || (!!resposta.id && item.id === resposta.id)
            );
            if (indice < 0) return [salva, ...itens];
            return itens.map((item, itemIndice) => itemIndice === indice ? salva : item);
          });
          this.integracaoSelecionada.set(salva);
          this.payloadValido.set(true);
          this.salvando.set(false);
          this.msgService.msgSucesso('Integração salva com sucesso!');
        },
        error: erro => {
          this.salvando.set(false);
          this.msgService.msgErro(
            erro?.error?.message ?? 'Não foi possível salvar a integração. Revise os dados informados.'
          );
        }
      });
  }

  cancelar() {
    const integracao = this.integracaoSelecionada();
    if (!integracao) return;

    if (integracao.novo) {
      this.integracoes.update(itens => itens.filter(item => item.chave !== integracao.chave));
      this.integracaoSelecionada.set(null);
      return;
    }
    this.reiniciarCarregamento();
  }

  excluir(integracao: IntegracaoView, evento: Event) {
    evento.stopPropagation();
    if (integracao.novo || !integracao.id) {
      this.integracoes.update(itens => itens.filter(item => item.chave !== integracao.chave));
      if (this.integracaoSelecionada()?.chave === integracao.chave) {
        this.integracaoSelecionada.set(null);
      }
      return;
    }

    const dialogRef = this.dialog.open(ModalConfirmacaoComponent, {
      data: {
        texto: 'Tem certeza que deseja inativar esta integração?',
        subTexto: 'Ela deixará de ficar disponível para este grupo.'
      }
    });
    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmado => {
        if (confirmado) this.confirmarExclusao(integracao);
      });
  }

  private confirmarExclusao(integracao: IntegracaoView) {
    this.integracaoService.inativar(integracao.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msgService.msgSucesso('Integração inativada com sucesso.');
          this.reiniciarCarregamento();
        },
        error: erro => this.msgService.msgErro(
          erro?.error?.message ?? 'Não foi possível inativar a integração selecionada.'
        )
      });
  }

  private atualizarSelecionada(alterar: (integracao: IntegracaoView) => void) {
    const atual = this.integracaoSelecionada();
    if (!atual) return;
    const copia = Object.assign(new IntegracaoView(), atual);
    alterar(copia);
    this.integracaoSelecionada.set(copia);
    this.integracoes.update(itens =>
      itens.map(item => item.chave === copia.chave ? copia : item)
    );
  }

  private reiniciarCarregamento() {
    this.cicloCarregamento++;
    this.proximaPagina = 0;
    this.possuiMais.set(true);
    this.carregando.set(false);
    this.carregandoMais.set(false);
    this.integracoes.set([]);
    this.integracaoSelecionada.set(null);
    this.carregarIntegracoes();
  }

  private paraView(modelo: IntegracaoModel): IntegracaoView {
    const view = Object.assign(new IntegracaoView(), modelo);
    view.chave = this.contadorChave++;
    view.novo = false;
    view.variaveis = (modelo.variaveis ?? [])
      .map(variavel => Object.assign(new IntegracaoVariavelModel(), variavel));
    if (!view.personalizada) {
      this.sincronizarVariaveisDoPayload(view, view.payload ?? '');
    }
    return view;
  }

  private urlValida(valor?: string): boolean {
    const url = (valor ?? '').trim();
    if (!url) return false;
    try {
      const analisada = new URL(url);
      return analisada.protocol === 'http:' || analisada.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private jsonObjetoValido(payload?: string): boolean {
    try {
      const valor = JSON.parse(payload ?? '');
      return valor !== null && typeof valor === 'object' && !Array.isArray(valor);
    } catch {
      return false;
    }
  }

  private jsonObjetoPossuiCampos(payload?: string): boolean {
    try {
      const valor = JSON.parse(payload ?? '');
      return valor !== null
        && typeof valor === 'object'
        && !Array.isArray(valor)
        && Object.keys(valor).length > 0;
    } catch {
      return false;
    }
  }

  private payloadPossuiConteudo(payload?: string): boolean {
    const conteudo = (payload ?? '').replace(/\s/g, '');
    return conteudo !== '' && conteudo !== '{}';
  }

  private obterMarcadores(payload?: string): Set<number> {
    const marcadores = new Set<number>();
    const regex = /\{\{(\d+)}}/g;
    let resultado: RegExpExecArray | null;
    while ((resultado = regex.exec(payload ?? '')) !== null) {
      marcadores.add(Number(resultado[1]));
    }
    return marcadores;
  }
}
